import { Granularity } from 'bindings/time/Granularity';
import GoogleMapReact from 'google-map-react';
import flag from 'images/icon/flag.svg';
import home from 'images/icon/home.svg';
import LayersImage from 'images/icon/map/layers.svg';
import * as moment from 'moment';
import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AProfile } from '../../atoms/global/users';
import { InputAddressResult } from '../../components_v2/input/model/Model';
import { getLocalStorage } from '../../helpers/localStorage';
import { Dot } from '../../styles/global/css/Dot';
import { GreySidely, PrimaryColor, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { HoveredEvent } from '../calendar/CalendarView';
import { colorTypeMapping } from '../calendar/model/Model';
import { FlexDiv } from '../products/style';
import MapPin, { MapDot, MapDotType, MapPinWithImage, MAP_DEFAULT_PIN_SIZE } from './MapPin';
import { HotelNightConstraint, initWaypoint } from './modalRight/ModalCalendar';
import { MapEvent, MapEventOptions, Position } from './modalRight/model';
import { Hiden, LegendContainer, LegendItem } from './modalRight/style';
import { GoogleLocation } from './model';
import { MapStyleDefault, MAP_STYLE_LIST } from './style';

let mapStyleVar = parseInt(localStorage.getItem('mapStyle') ?? '0');

const PinPopup = styled(FlexDiv)`
	position: absolute;
	top: 0;
	transform: translate(-50%,calc(-100% - 5px));
	left: 16px;
`;

type EventConfig = {
	events: MapEvent[];
	origin?: Position | undefined;
	destination?: Position | undefined;
}

function getEventsConfig(
	allEvents: MapEvent[],
	time: moment.Moment,
	hotelNights: HotelNightConstraint | undefined,
	storageOrigin: InputAddressResult | undefined,
	storageDestination: InputAddressResult | undefined,
	options: GranularityOption
): EventConfig {
	const events = allEvents.filter(e => options.isInPart(time, e.time)).sort((a, b) => a.time.diff(b.time));
	if (!events.length) return {
		events
	};
	const yesterday = events[0].time.clone().subtract(1, 'day');
	const lastYesterdayEvent = allEvents.reduce<MapEvent | undefined>((acc, cur) => {
		if (cur.time.isSame(yesterday, 'day') && (acc?.time.isBefore(cur.time) ?? true)) {
			return cur;
		}
		return acc;
	}, undefined);

	const dayTime = events[events.length - 1].time.format('YYYY-MM-DD');
	const yesterdayDayTime = yesterday.format('YYYY-MM-DD');
	const hotelNight = hotelNights?.nights.find(night => night.date === dayTime);
	const yesterdayHotelNight = hotelNights?.nights.find(night => night.date === yesterdayDayTime);

	let origin: Position | undefined = undefined;
	let destination: Position | undefined = undefined;

	if (hotelNight || hotelNights?.allNights) {
		const lat = hotelNight?.address?.latitude ?? storageDestination?.latitude;
		const lng = hotelNight?.address?.longitude ?? storageDestination?.longitude;
		if (lat && lng) destination = { lat, lng };
	} else {
		const lat = storageDestination?.latitude;
		const lng = storageDestination?.longitude;
		if (lat && lng) destination = { lat, lng };
	}

	if (yesterdayHotelNight || hotelNights?.allNights) {
		const lat = yesterdayHotelNight?.address?.latitude ?? lastYesterdayEvent?.lat ?? storageOrigin?.latitude;
		const lng = yesterdayHotelNight?.address?.longitude ?? lastYesterdayEvent?.lng ?? storageOrigin?.longitude;
		if (lat && lng) origin = { lat, lng };
	} else {
		const lat = storageOrigin?.latitude;
		const lng = storageOrigin?.longitude;
		if (lat && lng) origin = { lat, lng };
	}
	return ({
		events,
		origin,
		destination
	});

}

export const MapPinWrapper = styled(Hiden) <{ hovered?: boolean, size?: number, center?: boolean }>`
	position: relative;
	cursor: pointer;
	${p => p.hovered ? 'z-index: 1;' : ''}
	translate: ${p => p.center ? '-50% -50%' : '-50% -100%'};
	${p => `
		height: ${p.size ?? MAP_DEFAULT_PIN_SIZE}px;
		width: ${p.size ?? MAP_DEFAULT_PIN_SIZE}px;
	`}

	&:hover {
		z-index: 1;
	}
`;

function EventPin(props: { event: MapEvent, position?: number, lat: number, lng: number, hoveredEvent?: HoveredEvent, setHoveredEvent?: (event: HoveredEvent | undefined) => void }) {
	const { event, position, setHoveredEvent, hoveredEvent } = props;
	const isHovered = hoveredEvent?.id == props.event.eventId.toString();
	return <MapPinWrapper
		hovered={isHovered}
		onMouseEnter={() => setHoveredEvent?.({ id: event.eventId.toString(), companyId: event.companyId })}
		onMouseLeave={() => setHoveredEvent?.(undefined)}
	>
		{event.pinImageString
			? <MapPinWithImage pinColor={isHovered ? SidelyBlack : event.color ?? PrimaryColor} imageString={event.pinImageString} pinSize={MAP_DEFAULT_PIN_SIZE} />
			: <MapPin size={MAP_DEFAULT_PIN_SIZE} color={isHovered ? SidelyBlack : event.color ?? PrimaryColor} />}
		<PinPopup flow='column' className='text-tooltips font-weight-bold show-on-hover'>
			<div>{event.companyName}</div>
			<div>{event.time.format('LT')}</div>
		</PinPopup>
		{position && <DotLassoPositionWrapper color={event.color ?? PrimaryColor}>
			{position}
		</DotLassoPositionWrapper>}
	</MapPinWrapper>;
};

const DotLassoPositionWrapper = styled.div<{ color: string }>`
	position: relative;
	background-color: ${p => p.color};
	color: white;
	width: 20px;
	height: 20px;
	display: flex;
	justify-content: center;
  	align-items: center;
   	translate: -50%;
   	left: 50%;
   	top: 5px;
    border-radius: 50%;
`;

export function CalendarMapView<D extends React.Key>(props: {
	mapEvents: MapEventOptions[]
	useGranularity?: boolean
	hoveredEvent?: HoveredEvent
	setHoveredEvent?: (event: HoveredEvent | undefined) => void,
	width?: string | number,
	height?: string | number,
	noLink?: boolean,
	/** The date the map is looking at. With `useGranularity`, the period
	 * (`granularity`) around it is drawn: one trip per day, coloured by its
	 * `tripPart`. The caller owns both: the map has no selector of its own,
	 * it follows the calendar next to it. */
	time: moment.Moment,
	granularity?: Granularity,
	/** Trip parts the user hid (keys from `tripPart`); see `useTripFilter`. */
	hiddenTrips?: ReadonlySet<string>,
	/** Called with a date inside the part the user clicked in the legend. */
	onToggleTrip?: (date: Date) => void,
	topRightButtons?: JSX.Element,
	drawRoute: boolean,
	hotelNights?: HotelNightConstraint,
	dots?: MapDotType<D>[],
	origin?: GoogleLocation
}) {
	const profile = useRecoilValue(AProfile);
	const { mapEvents, hoveredEvent, setHoveredEvent, time } = props;
	const granularity: Granularity = props.granularity ?? 'day';
	const [zoom, setZoom] = React.useState<number>(6);
	const [center, setCenter] = React.useState<Position>(props.origin ?? { lat: 48.85356482880834, lng: 2.348096962645894 });
	const [map, setMap] = React.useState(null);
	const [maps, setMaps] = React.useState<any>(null);
	const [directions, setDirections] = React.useState<any>(undefined);
	const [mapStyle, setMapStyle_] = React.useState<number>(MAP_STYLE_LIST[mapStyleVar] ? mapStyleVar : 0);
	const setMapStyle = (n: number) => {
		setMapStyle_(n);
		localStorage.setItem('mapStyle', n.toString());
	};
	// Bumped on every route draw; a Directions callback from an older draw
	// finds a different value and drops its result.
	const drawGeneration = React.useRef(0);

	function createMapOptions(maps) {
		return {
			zoomControlOptions: {
				position: maps.ControlPosition.RIGHT_TOP,
				style: maps.ZoomControlStyle.SMALL
			},
			styles: MAP_STYLE_LIST[mapStyle][0] ?? MapStyleDefault
		};
	}

	React.useEffect(() => {
		if (props.origin) {
			setCenter(props.origin);
		}
	}, [props.origin]);


	function LayerModeFn(controlDiv) {
		// Set CSS for the control border.
		const controlUI = document.createElement('div');
		controlUI.style.backgroundColor = SurfaceColor;
		controlUI.style.border = `2px solid ${SurfaceColor}`;
		controlUI.style.borderRadius = '3px';
		controlUI.style.boxShadow = `0 2px 6px color-mix(in oklab, ${SidelyBlack} 30%, transparent)`;
		controlUI.style.cursor = 'pointer';
		controlUI.style.padding = '5px';
		controlUI.style.marginRight = '10px';
		controlUI.style.textAlign = 'center';
		controlUI.title = 'View';
		controlUI.style.width = '40px';
		controlUI.style.height = '40px';
		controlDiv.appendChild(controlUI);

		// Set CSS for the control interior.
		const controlImg = document.createElement('img');
		controlImg.src = LayersImage;
		controlImg.style.width = '27px';
		controlImg.style.height = '27px';
		controlUI.appendChild(controlImg);

		// Activate / Deactivate Lasso
		controlUI.addEventListener('click', () => {
			mapStyleVar = mapStyleVar == 0 ? 1 : 0;
			setMapStyle(mapStyleVar);
		});
	}

	const currentEvents = React.useMemo(() => {
		const rawStorageOrigin = initWaypoint('origin', profile);
		const rawStorageDestination = initWaypoint('destination', profile);
		// When the caller explicitly hands in a depot (props.origin), use it for
		// BOTH endpoints. That's the TSP case: the vehicle starts and ends at the
		// same point. Without this, the calendar map keeps pulling destination
		// from the 'destination' localStorage / profile fallback — which renders
		// the daily route ending at the user's profile address (a "flag" pin)
		// even after the user has overridden the home in the optimization UI.
		const storageOrigin: InputAddressResult | undefined = props.origin
			? { ...(rawStorageOrigin ?? {}), latitude: props.origin.lat, longitude: props.origin.lng } as InputAddressResult
			: rawStorageOrigin;
		const storageDestination: InputAddressResult | undefined = props.origin
			? { ...(rawStorageDestination ?? {}), latitude: props.origin.lat, longitude: props.origin.lng } as InputAddressResult
			: rawStorageDestination;

		if (!props.useGranularity || mapEvents.length !== 1) {
			return mapEvents.map(eo => ({
				...eo,
				...getEventsConfig(eo.events, time, props.hotelNights, storageOrigin, storageDestination, DAY_OPTIONS),
			}));
		}
		const options = granularityOptions(granularity);
		let currentTime: moment.Moment | undefined = options.lowerBound(moment(time));
		const res: (EventConfig & { lineColor?: string | undefined })[] = [];
		while (currentTime) {
			const config = getEventsConfig(mapEvents[0].events, currentTime, props.hotelNights, storageOrigin, storageDestination, options);
			const part = tripPart(currentTime, granularity);
			if (config.events.length > 0 && !props.hiddenTrips?.has(part.key)) {
				const partColor = part.color;
				config.events.forEach(event => {
					event.color = partColor;
				});
				res.push({
					...config,
					lineColor: partColor,
				});
			}
			currentTime = options.nextPart(currentTime);
		}
		return res;
	}, [time.valueOf(), mapEvents, granularity, props.hotelNights, props.origin, props.hiddenTrips]);

	function drawRoutes(localMap?, localDirections?: any[], localMaps?) {
		if (!props.drawRoute) return;
		const newDirection = localDirections ?? directions;
		const currentMap = localMap ?? map;
		const currentMaps = localMaps ?? maps;
		localDirections?.forEach(e => e?.directionsRenderer?.setMap(null));
		directions?.forEach(e => e?.directionsRenderer?.setMap(null));
		let travelMode;
		switch (getLocalStorage('transportation-mode')) {
			case 'pedestrian':
				travelMode = currentMaps?.TravelMode.WALKING;
				break;
			case 'car':
			case 'auto':
			case 'truck':
				travelMode = currentMaps?.TravelMode.DRIVING;
				break;
			case 'bicycle':
			default:
				travelMode = currentMaps?.TravelMode.BICYCLING;
				break;
		}
		const jobs: { directions: any, request: { origin: Position, destination: Position, waypoints: unknown[], travelMode: unknown } }[] = [];
		currentEvents.forEach((eventOption, i) => {
			const events = eventOption.events;
			let currentDirections = localDirections?.[i] ?? directions?.[i];
			if (!localDirections?.[i] && currentMaps) {
				currentDirections = {
					directionsService: new currentMaps.DirectionsService(),
					directionsRenderer: new currentMaps.DirectionsRenderer({
						suppressMarkers: true,
						suppressBicyclingLayer: true,
						polylineOptions: {
							strokeColor: eventOption.lineColor ?? PrimaryColor
						}
					})
				};
				newDirection[i] = currentDirections;
			}
			currentDirections?.directionsRenderer?.setMap(null);
			if (!currentMap || !events || !currentDirections || !currentMaps || events.length == 0) return;

			const waypoints = events.filter(e => !e.noLink).map(event => ({
				location: { lat: event.lat, lng: event.lng },
				stopover: true
			}));
			if (waypoints.length == 0) return;
			const origin: Position = eventOption.origin ?? { lat: waypoints[0].location.lat, lng: waypoints[0].location.lng };
			// Default the route's destination so the polyline actually closes back home:
			//   - Caller-provided destination wins (explicit user choice).
			//   - Otherwise, with a hotel-night config that isn't "all nights in hotels",
			//     close the loop at origin (vehicle returns to depot every day).
			//   - Otherwise default to the last waypoint (one-way trip).
			//
			// Previously the dangling `else` clobbered an explicit destination AND
			// never reached the "close at origin" branch when hotelNights was absent,
			// so the route stopped at the last waypoint regardless of intent.
			let destination: Position;
			if (eventOption.destination) {
				destination = eventOption.destination;
			} else if (props.hotelNights && !props.hotelNights.allNights) {
				destination = origin;
			} else {
				destination = waypoints[waypoints.length - 1].location;
			}
			jobs.push({ directions: currentDirections, request: { origin, destination, waypoints, travelMode } });
		});
		// One Directions request in flight at a time: a month holds twenty-odd
		// trips and firing them all at once runs into OVER_QUERY_LIMIT. A newer
		// draw supersedes whatever is still queued.
		const generation = ++drawGeneration.current;
		const next = () => {
			const job = jobs.shift();
			if (!job || generation !== drawGeneration.current) return;
			job.directions.directionsService.route(job.request, (result, status) => {
				if (generation !== drawGeneration.current) return;
				if (status === currentMaps.DirectionsStatus.OK) {
					job.directions.directionsRenderer.setMap(currentMap);
					job.directions.directionsRenderer.setDirections(result);
				} else {
					console.error('error fetching directions', result);
				}
				next();
			});
		};
		next();
		if (newDirection) {
			setDirections([...newDirection]);
		}
	}

	React.useEffect(() => {
		if (!props.noLink) drawRoutes();
	}, [JSON.stringify(currentEvents), getLocalStorage('transportation-mode'), props.drawRoute]);

	function handleApiLoaded(map, maps) {
		const layersDiv = document.createElement('div');
		LayerModeFn(layersDiv);
		map.controls[maps.ControlPosition.RIGHT_TOP].push(layersDiv);
		const directionsServices: any[] = currentEvents.map(_ => new maps.DirectionsService());
		const directionsRenderers: any[] = currentEvents.map(eo => new maps.DirectionsRenderer({
			suppressMarkers: true,
			suppressBicyclingLayer: true,
			polylineOptions: {
				strokeColor: eo.lineColor ?? PrimaryColor
			}
		}));
		directionsRenderers.forEach(dr => dr.setMap(map));
		const directions = currentEvents.map((_, i) => ({ directionsService: directionsServices[i], directionsRenderer: directionsRenderers[i] }));
		setDirections(directions);
		setMap(map);
		setMaps(maps);
		if (!props.noLink) drawRoutes(map, directions, maps);
	}

	const drawOrigin = React.useCallback(() => {
		if (!props.origin && !currentEvents.length) return <></>;
		const origin = props.origin ?? currentEvents[0].origin;
		if (!origin) return <></>;
		const imageWidth = 32;
		return <MapDot
			lat={origin.lat}
			lng={origin.lng}
			size='fit-content'
			color='transparent'
			name={<Translate id='departure' />}
			popupTop={imageWidth / 2 + 5}
		>
			<img src={home} width={imageWidth} style={{ translate: '-50% -50%' }} />
		</MapDot>;
	}, [currentEvents, granularity, props.origin]);

	const drawDestination = React.useCallback(() => {
		const res: React.ReactNode[] = [];
		for (const [index, events] of currentEvents.entries()) {
			if (!events.destination || (events.origin?.lat === events.destination?.lat && events.origin?.lng === events.destination?.lng)) continue;
			res.push(
				<div
					key={`crime-destination[${index}]`}
					// eslint-disable-next-line react/no-unknown-property
					lat={events.destination.lat}
					// eslint-disable-next-line react/no-unknown-property
					lng={events.destination.lng}
				>
					<Hiden>
						<img src={flag} width={32} />
						<div className='text-tooltips font-weight-bold show-on-hover'>
							<Translate id='destination' />
						</div>
					</Hiden>
				</div>);
		}
		return res;
	}, [time, props.hotelNights, currentEvents]);


	return <div style={{ height: props.height, width: props.width, position: 'relative' }}>
		<GoogleMapReact
			bootstrapURLKeys={{
				key: 'AIzaSyBkQkAoGTXdwr-MrDWQYQPt0zvAw57T7p0'
			}}
			defaultCenter={center}
			center={center}
			defaultZoom={zoom}
			zoom={zoom}
			options={createMapOptions}
			onChange={val => {
				setZoom(val.zoom);
				setCenter(val.center);
			}}
			yesIWantToUseGoogleMapApiInternals
			onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
		>
			{props.dots?.map(dot => {
				if (!props.useGranularity || !granularity || !dot.time)
					return <MapDot
						key={dot.id}
						lat={dot.latitude}
						lng={dot.longitude}
						dot={dot}
					/>;
				const dotMomentTime = moment(dot.time);
				if (!granularityOptions(granularity).isInGranularity(time, dotMomentTime)) return undefined;
				const part = tripPart(dotMomentTime, granularity);
				if (props.hiddenTrips?.has(part.key)) return undefined;
				const color = part.color;
				return <MapDot
					key={dot.id}
					lat={dot.latitude}
					lng={dot.longitude}
					dot={dot}
				>
					{dot.childrenWithColor?.(color)}
				</MapDot>;
			})}
			{currentEvents.map(currentEvents => currentEvents.events.filter(e => !e.noIndex).map((event, i) => <EventPin key={`crime-${event.eventId}`} event={event} position={i + 1} lat={event.lat} lng={event.lng} hoveredEvent={hoveredEvent} setHoveredEvent={setHoveredEvent} />))}
			{currentEvents.map(currentEvents => currentEvents.events.filter(e => e.noIndex).map(event => <EventPin key={`crime-${event.eventId}`} event={event} lat={event.lat} lng={event.lng} hoveredEvent={hoveredEvent} setHoveredEvent={setHoveredEvent} />))}
			{(currentEvents.some(ce => ce.events.length > 0) || props.origin) && drawOrigin()}
			{currentEvents.some(ce => ce.events.length > 0) && drawDestination()}
		</GoogleMapReact>
		<div style={{ position: 'absolute', top: '10px', left: '10px', display: 'flex', gap: '10px' }}>
			{props.useGranularity && granularity !== 'day' && mapEvents.length === 1 && <TripLegend
				granularity={granularity}
				time={time}
				events={mapEvents[0].events}
				hiddenTrips={props.hiddenTrips}
				onToggleTrip={props.onToggleTrip}
			/>}
			<FlexDiv flow='column' gap='10px'>{props.topRightButtons}</FlexDiv>
		</div>
	</div>;
}

export type TripPart = { key: string, color: string };

/**
 * What one calendar header dot or legend entry stands for: a day in day and
 * week view, a week in month view. The colour is shared by the route
 * polyline, its pins and the dot; the key names the part in a hidden set.
 */
export function tripPart(date: moment.MomentInput, granularity: Granularity): TripPart {
	const m = moment(date);
	if (granularity === 'month') {
		const week = moment(m).startOf('week');
		const index = week.diff(moment(m).startOf('month').startOf('week'), 'weeks');
		return { key: `week:${week.format('YYYY-MM-DD')}`, color: colorTypeMapping[index].color };
	}
	return { key: `day:${m.format('YYYY-MM-DD')}`, color: colorTypeMapping[m.weekday()].color };
}

/**
 * The quick display filter: the trips the user hid by clicking a day's dot
 * in the calendar header or a week in the month legend. `dotColor` is the
 * dot of a day that holds a trip: its part's colour, grey once hidden.
 */
export function useTripFilter(granularity: Granularity) {
	const [hidden, setHidden] = React.useState<ReadonlySet<string>>(() => new Set());
	const toggle = React.useCallback((date: Date) => {
		const { key } = tripPart(date, granularity);
		setHidden(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, [granularity]);
	const dotColor = React.useCallback((date: Date) => {
		const part = tripPart(date, granularity);
		return hidden.has(part.key) ? GreySidely : part.color;
	}, [granularity, hidden]);
	return { hidden, toggle, dotColor };
}

/**
 * The map's key: one entry per part of the period that holds a trip, the
 * days in week view, the weeks in month view. Clicking an entry hides or
 * shows that part: the same filter as the dot in the calendar's day header.
 */
function TripLegend(props: { granularity: Granularity, time: moment.Moment, events: MapEvent[], hiddenTrips?: ReadonlySet<string>, onToggleTrip?: (date: Date) => void }) {
	const entries = React.useMemo(() => {
		if (props.granularity === 'month') {
			const monthStart = moment(props.time).startOf('month');
			const monthEnd = moment(monthStart).add(1, 'month');
			const weeks: moment.Moment[] = [];
			for (let week = moment(monthStart).startOf('week'); week.isBefore(monthEnd); week = moment(week).add(1, 'week')) weeks.push(week);
			return weeks
				.filter(week => props.events.some(e => e.time.isSame(week, 'week') && e.time.isSame(monthStart, 'month')))
				.map(week => ({ date: week, label: `${translateToString('week_short')}${week.week()} · ${week.format('D MMM')} – ${moment(week).endOf('week').format('D MMM')}` }));
		}
		const options = granularityOptions(props.granularity);
		const days: moment.Moment[] = [];
		for (let day: moment.Moment | undefined = options.lowerBound(moment(props.time)); day; day = options.nextPart(day)) days.push(day);
		return days
			.filter(day => props.events.some(e => e.time.isSame(day, 'day')))
			.map(day => ({ date: day, label: day.format('dddd D MMM') }));
	}, [props.granularity, props.time.valueOf(), props.events]);
	if (!entries.length) return null;
	return <LegendContainer>
		{entries.map(({ date, label }) => {
			const part = tripPart(date, props.granularity);
			const hidden = props.hiddenTrips?.has(part.key) ?? false;
			return <LegendItem key={part.key} desactivated={hidden} onClick={() => props.onToggleTrip?.(date.toDate())}>
				<Dot color={hidden ? GreySidely : part.color} size='10px' />
				{label}
			</LegendItem>;
		})}
	</LegendContainer>;
}

type GranularityOption = {
	lowerBound: (t: moment.Moment) => moment.Moment,
	/** Two moments of the same trip (a trip is one day). */
	isInPart: (base: moment.Moment, compare: moment.Moment) => boolean,
	isInGranularity: (base: moment.Moment, compare: moment.Moment) => boolean,
	nextPart: (t: moment.Moment) => moment.Moment | undefined,
}

function nextDayWithin(unit: 'week' | 'month'): GranularityOption['nextPart'] {
	return t => {
		const upperBound = moment(t).startOf(unit).add(1, unit);
		const newTime = moment(t).add(1, 'day');
		if (newTime.isSameOrAfter(upperBound)) {
			return undefined;
		}
		return newTime;
	};
}

const DAY_OPTIONS: GranularityOption = {
	lowerBound: t => t.startOf('day'),
	isInPart: (t1, t2) => t1.isSame(t2, 'day'),
	isInGranularity: (t1, t2) => t1.isSame(t2, 'day'),
	nextPart: _ => undefined,
};

const GRANULARITY_OPTIONS: Partial<Record<Granularity, GranularityOption>> = {
	day: DAY_OPTIONS,
	week: {
		lowerBound: t => t.startOf('week'),
		isInPart: (t1, t2) => t1.isSame(t2, 'day'),
		isInGranularity: (t1, t2) => t1.isSame(t2, 'week'),
		nextPart: nextDayWithin('week'),
	},
	month: {
		lowerBound: t => t.startOf('month'),
		isInPart: (t1, t2) => t1.isSame(t2, 'day'),
		isInGranularity: (t1, t2) => t1.isSame(t2, 'month'),
		nextPart: nextDayWithin('month'),
	},
};

function granularityOptions(granularity: Granularity): GranularityOption {
	return GRANULARITY_OPTIONS[granularity] ?? DAY_OPTIONS;
}
